import Button from '../../../../../components/form-control/Button';
import { ShieldCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useState } from 'react';
import {
  assignNonMotorPolicyManager} from '../../../../../api/requests';
import SelectDropdown, {
  ISelectDropdownItem
} from '../../../../../components/form-control/SelectDropdown';
import { getFormattedAccessRight, showToast } from '../../../../../utils/miscFunctions';
import { useQuery } from '@tanstack/react-query';
import usePartner from '../../../../../hooks/partnerHook';
import { motion } from 'framer-motion';

interface IAssignPolicyProps {
  onProceed: () => void;
  onClose: () => void;
  nonMotor: any;
}

const AssignNonMotorPolicy = ({ onClose, onProceed, nonMotor }: IAssignPolicyProps) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { getAllCompanyStaff, fetchAllCompaniesByType } = usePartner();
  const id = nonMotor?.staff?.company?.id || nonMotor?.owner?.id;

  const { data: admins, isLoading: loadingAdmins } = useQuery({
    queryKey: ['getAllCompanyStaff', id],
    queryFn: () => getAllCompanyStaff(Number(id)),
    enabled: !!id
  })


  const submitHandler = async (values: any) => {
    setSubmitting(true);

    try {

      const response = await assignNonMotorPolicyManager(nonMotor?.policyNumber, Number(values.id));
      if (response) {
        showToast.success(response?.message || 'Policy assigned successfully');
        onProceed();
      }
    } catch (error: any) {
      showToast.error(error?.description || 'Failed to assign nonMotor');
    } finally {
      setSubmitting(false);
    }

  };
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className="bg-white dark:bg-darkBg rounded-2xl shadow-xl max-w-md w-full mx-auto"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 dark:border-darkBg-border">
        <div className="flex items-center space-x-3">
          
          <div className='flex items-center gap-3'>
            <ShieldCheckIcon className="w-5 h-5 text-purple-600" />
            <p className="text-sm text-gray-500 mt-0.5">
              Policy #{nonMotor?.policyNumber || 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-5 dark:text-darkBg-mainText">
        {/* Warning Message */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5"
        >
          <div className="flex items-start">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 mt-0.5 mr-2 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-amber-800">Important Notice</p>
              <p className="text-amber-700 mt-1">
                {'Select a new manager to oversee this non-motor. The current manager will be replaced'}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Current Assignment Info */}
        {(nonMotor?.manager || nonMotor?.owner) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="bg-gray-50 dark:bg-darkBg rounded-lg p-3 mb-5"
          >
            <p className="text-xs font-medium text-gray-500 dark:text-darkBg-mainText uppercase tracking-wider mb-2">
              Current Manager
            </p>
            <p className="text-sm font-medium text-gray-900 dark:text-darkBg-mainText">
              {nonMotor?.manager?.fullName || 'Not assigned'}
            </p>
          </motion.div>
        )}

        <Formik
          initialValues={{
            id: ''
          }}
          validationSchema={Yup.object({
            id: Yup.mixed().required(`Please select a new 'manager`)
          })}
          validateOnChange={true}
          validateOnBlur={true}
          onSubmit={(values) => {
            submitHandler(values);
          }}>
          {({ values, errors, handleSubmit, handleChange, setFieldValue, handleBlur, touched, isSubmitting }) => {
            return (
            <form onSubmit={handleSubmit} className="space-y-4">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                <SelectDropdown
                  label={"Select new manager"}
                  name="id"
                  list={admins?.map((admin: any) => ({
                    name: `${admin.fullName} ${admin.role ? `(${getFormattedAccessRight(admin.role)})` : ''}`,
                    value: admin.id
                  })) || []}
                  listSelectedValue={values.id}
                  onChange={(val: ISelectDropdownItem | null) => {
                    setFieldValue('id', val);
                  }}
                  search
                  searchPlaceHolder='Search by name'
                  onBlur={handleBlur}
                  placeholder={"Select new manager"}
                  error={touched.id && errors.id ? errors.id : undefined}
                  helperText={touched.id && errors.id ? errors.id : undefined}
                  required
                  disabled={loadingAdmins}
                />
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
                className="flex gap-5 pt-2"
              >
                <Button
                  type="button"
                  label="Cancel"
                  bgColor="secondary"
                  onClick={onClose}
                  disabled={submitting}
                  fullWidth
                />
                <Button
                  type="submit"
                  label={submitting ? `Assigning...` : `Assign Manager`}
                  bgColor="primary"
                  loading={submitting}
                  disabled={submitting || !values.id || Object.keys(errors).length > 0}
                  fullWidth
                />
              </motion.div>
            </form>
          )}}
        </Formik>
      </div>
    </motion.div>
  );
};

export default AssignNonMotorPolicy;
